import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { DsToastService } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';

import { AdminModeService } from '../../../../auth/admin-mode/admin-mode.service';
import { ReadonlyService } from '../../../../auth/readonly/readonly.service';
import { FoundCustomer, FoundRetailCustomer, isRetailCustomer } from '../../../customer/model/found-customer.model';
import { CustomerCreationModalWrapper } from '../../../customer-creation/components/customer-creation-modal.wrapper';
import { SearchFormService } from '../../../customer-search-page/search-form.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { VerifyChoiceModalWrapper } from '../../../modal/verify-choice-modal/verify-choice-modal.wrapper';
import { SearchResultsComponent } from './components/search-results/search-results.component';
import { SearchResultMode } from './model/search-result-mode.model';

@Component({
  selector: 'cdf-search-results-container',
  imports: [SearchResultsComponent, CustomerCreationModalWrapper, VerifyChoiceModalWrapper],
  host: { class: 'd-block' },
  template: `
    <cdf-search-results
      [searchResults]="searchFormService.searchResults()"
      [searchResultMode]="searchResultMode()"
      [loadingCustomer]="loadingCustomer()"
      [readonly]="readonlyService.readonly()"
      [activeAdminDpNr]="adminModeService.activeAdminDpNr()"
      (openCustomerProfile)="tryOpenCustomerProfile($event, false)"
      (createNewCustomer)="customerCreationOpen.set(true)"
    />
    <cdf-customer-creation-modal-wrapper
      [(open)]="customerCreationOpen"
      [initialCustomerInfo]="searchFormService.getSearchParameterAsCustomer()"
      (openCustomerProfile)="openCustomerProfile.emit($event)"
    />
    <cdf-verify-choice-modal-wrapper
      [(open)]="askForImportConsentModalOpen"
      [headingText]="i18n('importToRetail.warningHeading')"
      [bodyText]="i18n('importToRetail.warningBody')"
      [verifyButtonText]="i18n('importToRetail.warningAgreeButton')"
      [buttonLoading]="verifyChoiceButtonLoading()"
      (verify)="tryOpenCustomerProfile(foundCustomerForConsent, true)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultsContainer {
  protected readonly searchFormService = inject(SearchFormService);
  protected readonly adminModeService = inject(AdminModeService);
  protected readonly readonlyService = inject(ReadonlyService);
  private readonly dsToastService = inject(DsToastService);
  private readonly logger = inject(NGXLogger);

  readonly searchResultMode = input<SearchResultMode.CUSTOMER_SEARCH | SearchResultMode.C2V_LINK>(
    SearchResultMode.CUSTOMER_SEARCH,
  );

  readonly openCustomerProfile = output<FoundRetailCustomer>();

  protected readonly i18n = useI18n();

  protected readonly customerCreationOpen = signal(false);
  protected readonly askForImportConsentModalOpen = signal(false);
  protected readonly verifyChoiceButtonLoading = signal(false);
  /**
   * Describes which customers profile is currently being opened and should display a loading state
   */
  protected readonly loadingCustomer = signal<FoundCustomer | undefined>(undefined);

  protected foundCustomerForConsent?: FoundCustomer;
  private toastId = 'SEARCH_RESULTS_TOAST';

  private readonly stopLoadingOnAskForImportModalClose = effect(() => {
    if (!this.askForImportConsentModalOpen()) {
      this.loadingCustomer.set(undefined);
    }
  });
  /**
   * Opens customer profile. If customer does only have a GCID but not accountNumber, handles verification of choice via user and import to Retail CRM.
   * @param foundCustomer customer whose profile is to be opened. Optional, as verification modal makes use of optional customer variable.
   * @param consentForImportGiven
   */
  protected async tryOpenCustomerProfile(foundCustomer: FoundCustomer | undefined, consentForImportGiven: boolean) {
    if (!foundCustomer) {
      this.logger.error(
        '#CDFTF0093 No customer provided for importing customer to Retail CRM and opening profile page.',
      );
      this.askForImportConsentModalOpen.set(false);
      this.showOpenCustomerProfileError();
      return;
    }

    if (!foundCustomer.accountNumber && !foundCustomer.gcid) {
      this.logger.error(
        '#CDFTF0101 Neither accountNumber nor GCID could be fetched for customer, which is required for opening a customer profile.',
      );
      this.askForImportConsentModalOpen.set(false);
      this.showOpenCustomerProfileError();
      return;
    }

    this.loadingCustomer.set(foundCustomer);

    if (!isRetailCustomer(foundCustomer)) {
      // CUSTAPP-607: A vehicle-based search must never trigger an ESO -> Retail import.
      // Vehicle results originate solely from Retail CRM, so a result without an accountNumber
      // here is unexpected and must NOT be imported from ESO CRM.
      if (this.searchFormService.isVehicleSearch()) {
        this.logger.error(
          '#CDFTF0258 Vehicle-based search result without a Retail account number cannot be imported from ESO CRM.',
        );
        this.askForImportConsentModalOpen.set(false);
        this.loadingCustomer.set(undefined);
        this.showOpenCustomerProfileError();
        return;
      }
      const gcid = foundCustomer.gcid;
      if (gcid && consentForImportGiven) {
        this.verifyChoiceButtonLoading.set(true);

        try {
          foundCustomer.accountNumber = await this.searchFormService.fetchAccountNumberWithImport(gcid);
        } finally {
          this.verifyChoiceButtonLoading.set(false);
        }

        if (!isRetailCustomer(foundCustomer)) {
          throw new Error('#CDFTF0091 No account number could be fetched.');
        }
      } else if (gcid && !consentForImportGiven) {
        // Store customer to access it later after consent of user for import to Retail was provided
        this.askUserForImportConsent(foundCustomer);
        return;
      } else {
        throw new Error('#CDFTF0101 Neither accountNumber not GCID could be fetched for customer.');
      }
    }
    this.openCustomerProfile.emit(foundCustomer);
    this.loadingCustomer.set(undefined);
  }

  private askUserForImportConsent(foundCustomer: FoundCustomer) {
    // Ask for consent of user to import customer to Retail CRM
    this.foundCustomerForConsent = foundCustomer;
    this.askForImportConsentModalOpen.set(true);
  }

  private showOpenCustomerProfileError() {
    this.dsToastService.pushToast({
      toastTitle: this.i18n('searchResults.openProfileErrorTitle'),
      toastText: this.i18n('searchResults.openProfileErrorMessage'),
      id: this.toastId,
      tone: 'critical',
    });
  }
}
